/**
 * 素材库批次列表组件
 * 用于展示和筛选素材库列表
 */

import type { ReactElement, ChangeEvent } from 'react'
import { useState, useEffect, useCallback } from 'react'
import { Search, Loader2, FolderOpen } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useCrawlBatches } from '@/hooks/useCrawlBatches'
import type { CrawlBatch, CrawlBatchPlatform } from '@/types/crawlBatch'
import { CRAWL_BATCH_PLATFORM } from '@/types/crawlBatch'

/**
 * MaterialLibraryBatchList 组件属性
 */
interface MaterialLibraryBatchListProps {
  /** 当前选中的批次 ID */
  selectedBatchId: string | null
  /** 选择批次回调 */
  onSelectBatch: (batch: CrawlBatch) => void
  /** 每个批次的选中数量 */
  countByBatch: Map<string, number>
}

/**
 * 素材库批次列表组件
 * @param props 组件属性
 * @returns 组件元素
 */
export function MaterialLibraryBatchList({
  selectedBatchId,
  onSelectBatch,
  countByBatch,
}: MaterialLibraryBatchListProps): ReactElement {
  // 搜索关键词（本地状态，用于防抖）
  const [searchInput, setSearchInput] = useState('')

  // 使用批次列表 Hook
  const {
    items,
    isLoading,
    error,
    platform,
    setPlatform,
    setRemark,
  } = useCrawlBatches(50)

  /**
   * 防抖处理搜索输入
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setRemark(searchInput)
    }, 300)

    return (): void => {
      clearTimeout(timer)
    }
  }, [searchInput, setRemark])

  /**
   * 处理搜索输入变化
   */
  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    setSearchInput(e.target.value)
  }, [])

  /**
   * 处理平台筛选变化
   */
  const handlePlatformChange = useCallback(
    (value: string): void => {
      setPlatform(value === 'all' ? '' : (value as CrawlBatchPlatform))
    },
    [setPlatform]
  )

  /**
   * 格式化日期
   * @param dateStr 日期字符串
   * @returns 格式化后的日期
   */
  const formatDate = useCallback((dateStr: string): string => {
    const date = new Date(dateStr)
    return `${String(date.getMonth() + 1)}/${String(date.getDate())}`
  }, [])

  return (
    <div className="flex h-full w-[280px] flex-col border-r">
      {/* 搜索和筛选 */}
      <div className="space-y-3 border-b p-3">
        {/* 搜索框 */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="搜索备注..."
            value={searchInput}
            onChange={handleSearchChange}
            className="pl-9"
          />
        </div>

        {/* 平台筛选 */}
        <Select
          value={platform || 'all'}
          onValueChange={handlePlatformChange}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="选择平台" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部平台</SelectItem>
            <SelectItem value={CRAWL_BATCH_PLATFORM.TEMU}>Temu</SelectItem>
            <SelectItem value={CRAWL_BATCH_PLATFORM.AMAZON}>Amazon</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* 批次列表 */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : error !== null ? (
          <div className="px-3 py-8 text-center text-sm text-destructive">
            {error}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-3 py-8 text-muted-foreground">
            <FolderOpen className="mb-2 h-8 w-8 opacity-50" />
            <p className="text-sm">暂无素材库</p>
          </div>
        ) : (
          <div className="p-2">
            {items.map((batch) => {
              const isSelected = selectedBatchId === batch.id
              const selectedCount = countByBatch.get(batch.id) ?? 0

              return (
                <div
                  key={batch.id}
                  role="button"
                  tabIndex={0}
                  onClick={(): void => {
                    onSelectBatch(batch)
                  }}
                  onKeyDown={(e): void => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onSelectBatch(batch)
                    }
                  }}
                  className={cn(
                    'mb-1 cursor-pointer rounded-lg px-3 py-2.5 transition-colors',
                    isSelected
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-muted'
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'truncate text-sm font-medium',
                            isSelected ? 'text-primary' : 'text-foreground'
                          )}
                        >
                          {batch.remark || '未命名'}
                        </span>
                        {selectedCount > 0 && (
                          <Badge
                            variant="default"
                            className="h-5 min-w-[20px] px-1.5 text-xs"
                          >
                            {selectedCount}
                          </Badge>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{batch.successCount} 张</span>
                        <span>·</span>
                        <span>{formatDate(batch.createdAt)}</span>
                        <span>·</span>
                        <span
                          className={cn(
                            batch.platform === CRAWL_BATCH_PLATFORM.TEMU
                              ? 'text-orange-600 dark:text-orange-400'
                              : 'text-blue-600 dark:text-blue-400'
                          )}
                        >
                          {batch.platform === CRAWL_BATCH_PLATFORM.TEMU
                            ? 'Temu'
                            : 'Amazon'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
